import { Link } from "react-router-dom";
import { Envelope, Phone, MapPin, InstagramLogo, LinkedinLogo, WhatsappLogo } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-[#EDEBE4] border-t border-[#1A1A1A]/10" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <span className="font-heading text-2xl tracking-[0.35em] text-[#1A1A1A] uppercase">
                Altura
              </span>
            </Link>
            <p className="text-[#1A1A1A]/70 text-sm leading-relaxed font-light">
              {t("footer.brand_desc")}
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("footer.services")}</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/services/deplacements" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-link-deplacements">
                  {t("footer.deplacements")}
                </Link>
              </li>
              <li>
                <Link to="/services/greet-meet" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-link-greet-meet">
                  {t("footer.greet_meet")}
                </Link>
              </li>
              <li>
                <Link to="/services/securite" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-link-securite">
                  {t("footer.securite")}
                </Link>
              </li>
              <li>
                <Link to="/services/transport-luxe" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-link-transport-luxe">
                  {t("footer.transport_luxe")}
                </Link>
              </li>
              <li>
                <Link to="/destinations" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-link-destinations">
                  Destinations
                </Link>
              </li>
              <li>
                <Link to="/experiences" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-link-experiences">
                  Expériences
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("footer.contact")}</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <MapPin size={16} weight="light" className="text-[#1A1A1A]" />
                <span className="text-[#1A1A1A]/80 text-sm">Paris, France</span>
              </li>
              <li className="flex items-center gap-3">
                <Envelope size={16} weight="light" className="text-[#1A1A1A]" />
                <a href="mailto:contact@alturaconciergerie.com" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-email">
                  contact@alturaconciergerie.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} weight="light" className="text-[#1A1A1A]" />
                <a href="tel:+33681969342" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-phone">
                  +33 6 81 96 93 42
                </a>
              </li>
              <li className="flex items-center gap-3">
                <WhatsappLogo size={16} weight="light" className="text-[#1A1A1A]" />
                <a href="https://wa.me/33681969342" target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A]/80 text-sm hover:text-[#1A1A1A] transition-colors" data-testid="footer-whatsapp">
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("footer.locations")}</h4>
            <ul className="space-y-4">
              <li className="text-[#1A1A1A]/80 text-sm">Paris</li>
              <li className="text-[#1A1A1A]/80 text-sm">Londres</li>
              <li className="text-[#1A1A1A]/80 text-sm">Madrid</li>
            </ul>
            <div className="flex gap-4 mt-8">
              <a href="#" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A] transition-colors" data-testid="footer-instagram">
                <InstagramLogo size={20} weight="light" />
              </a>
              <a href="#" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A] transition-colors" data-testid="footer-linkedin">
                <LinkedinLogo size={20} weight="light" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-[#1A1A1A]/10 mt-16 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[#1A1A1A]/50 text-xs">
            &copy; {new Date().getFullYear()} {t("footer.copyright")}
          </p>
          <div className="flex gap-6">
            <Link to="/mentions-legales" className="text-[#1A1A1A]/50 text-xs hover:text-[#1A1A1A]/70 transition-colors" data-testid="footer-legal-link">
              {t("footer.legal")}
            </Link>
            <Link to="/politique-de-confidentialite" className="text-[#1A1A1A]/50 text-xs hover:text-[#1A1A1A]/70 transition-colors" data-testid="footer-privacy-link">
              {t("footer.privacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
